import { useMemo, type ReactNode } from 'react';
import { useAuth } from 'context/AuthContext';
import { canViewAllTasks, isManagerUser } from 'utils/helpers/access/hasUserAccess';
import { TaskProvider } from '../TaskContext';
import { loadBoardFilters } from '../Board/helpers/boardFiltersStorage';
import {
    snapshotToLead,
    snapshotToObject,
    snapshotToUser,
} from '../Board/Kanban/helpers/boardFilterSnapshots';
import TasksViewBody, { BOARD_EXCLUDE_STATUSES } from './TasksViewBody';

export { BOARD_EXCLUDE_STATUSES };

export type TasksViewShellProps = {
    children: ReactNode;
    contentClassName?: string;
};

const TasksViewShell = ({
    children,
    contentClassName = 'task-board-page__content',
}: TasksViewShellProps) => {
    const { auth } = useAuth();
    const userId = Number(auth?.user?.ID || 0);
    const canFilterByUsers = canViewAllTasks(auth?.user);
    const canFilterRelated = isManagerUser(auth?.user);
    const storedFilters = useMemo(
        () => loadBoardFilters(userId, { excludeStatuses: BOARD_EXCLUDE_STATUSES }),
        [userId],
    );

    const initialExecutorUser = useMemo(
        () =>
            canFilterByUsers
                ? snapshotToUser(storedFilters.executorUsers?.[0] ?? null)
                : null,
        [canFilterByUsers, storedFilters.executorUsers],
    );
    const initialControllerUser = useMemo(
        () =>
            canFilterByUsers
                ? snapshotToUser(storedFilters.controllerUsers?.[0] ?? null)
                : null,
        [canFilterByUsers, storedFilters.controllerUsers],
    );
    const initialLead = useMemo(
        () =>
            canFilterRelated && !storedFilters.onlyPersonal
                ? snapshotToLead(storedFilters.lead)
                : null,
        [canFilterRelated, storedFilters.lead, storedFilters.onlyPersonal],
    );
    const initialObject = useMemo(
        () =>
            canFilterRelated && !storedFilters.onlyPersonal
                ? snapshotToObject(storedFilters.object)
                : null,
        [canFilterRelated, storedFilters.object, storedFilters.onlyPersonal],
    );

    return (
        <TaskProvider
            key={userId}
            noShowOlderTask
            noShowDeletedTask
            defaultOnPage={100}
            initialTasksStatus={storedFilters.status}
            initialTasksType={storedFilters.type}
            initialTasksCategory={storedFilters.category}
            initialTasksExecutor={canFilterByUsers ? storedFilters.executor : null}
            initialTasksController={canFilterByUsers ? storedFilters.controller : null}
            initialFilterLeadId={
                canFilterRelated && !storedFilters.onlyPersonal ? storedFilters.leadId : null
            }
            initialFilterObjectId={
                canFilterRelated && !storedFilters.onlyPersonal ? storedFilters.objectId : null
            }
            initialOnlyPersonal={storedFilters.onlyPersonal}
            initialOnlyMine={canFilterByUsers ? storedFilters.onlyMine : true}
        >
            <TasksViewBody
                initialExecutorUser={initialExecutorUser}
                initialControllerUser={initialControllerUser}
                initialLead={initialLead}
                initialObject={initialObject}
                canFilterByUsers={canFilterByUsers}
                contentClassName={contentClassName}
            >
                {children}
            </TasksViewBody>
        </TaskProvider>
    );
};

export default TasksViewShell;
